import React, { useCallback, useEffect, useState } from 'react';
import { Receipt, Plus, RefreshCw, Clock, CheckCircle2, XCircle, Banknote, FileText, Hourglass } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { easerGet, errorMessage, fmtDate, BACKEND_URL, getToken } from '../../lib/easerApi';
import { ExpenseDialog } from './ExpenseDialog';

export const EXPENSE_STATUS = {
  draft: { label: 'Brouillon', className: 'bg-[hsl(var(--secondary))] text-foreground', Icon: FileText },
  submitted: { label: "En attente d'approbation", className: 'bg-[hsl(var(--accent))] text-white', Icon: Hourglass },
  approved: { label: 'Approuvée', className: 'bg-[hsl(var(--brand-sage))] text-white', Icon: CheckCircle2 },
  paid: { label: 'Remboursée', className: 'bg-[hsl(var(--primary))] text-white', Icon: Banknote },
  refused: { label: 'Refusée', className: 'bg-destructive text-destructive-foreground', Icon: XCircle },
  cancelled: { label: 'Annulée', className: 'bg-muted text-muted-foreground', Icon: XCircle },
};

export const fmtEuro = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
const photoUrl = (localId, index) => `${BACKEND_URL}/api/easer/expenses/${localId}/photos/${index}?token=${encodeURIComponent(getToken())}`;

/** Notes de frais: list + follow-up of the Easer's expense reports stored in Dolibarr. */
export function EaserExpenses({ onUnauthorized }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await easerGet('/api/easer/expenses');
      setItems(res.data.items);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger vos notes de frais'));
      setItems([]);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const pending = (items || []).filter((i) => i.status === 'submitted');
  const pendingTotal = pending.reduce((s, i) => s + (i.total_ttc || 0), 0);

  return (
    <div className="space-y-4" data-testid="easer-expenses">
      <div className="flex items-center gap-2">
        <div>
          <h2 className="font-['Space_Grotesk'] text-lg font-semibold">Mes notes de frais</h2>
          <p className="text-xs text-muted-foreground">Saisie, tickets scannés et suivi du remboursement dans Dolibarr</p>
        </div>
        <Button variant="ghost" size="icon" className="h-9 w-9 ml-auto" onClick={load} aria-label="Actualiser" data-testid="expenses-refresh"><RefreshCw className="h-4 w-4" /></Button>
        <Button onClick={() => setCreating(true)} className="h-9 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="expenses-new"><Plus className="h-4 w-4 mr-1" /> Nouvelle note</Button>
      </div>

      {items && items.length > 0 && (
        <Card className="p-4 border-border shadow-sm flex items-center gap-3" data-testid="expenses-summary">
          <Clock className="h-5 w-5 text-[hsl(var(--accent))]" />
          <div className="text-sm">
            <p className="font-medium">{pending.length} note{pending.length > 1 ? 's' : ''} en attente d'approbation</p>
            <p className="text-muted-foreground">{fmtEuro(pendingTotal)} en cours de traitement par l'agence</p>
          </div>
        </Card>
      )}

      {items === null && <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>}
      {error && (
        <Card className="p-6 text-center">
          <p className="text-sm text-destructive" data-testid="expenses-error">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={load}>Réessayer</Button>
        </Card>
      )}
      {items !== null && !error && items.length === 0 && (
        <Card className="p-8 text-center" data-testid="expenses-empty">
          <Receipt className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Aucune note de frais</p>
          <p className="text-sm text-muted-foreground mt-1">Carburant, péage, repas, fournitures... Photographiez votre ticket et envoyez-le à l'agence.</p>
          <Button onClick={() => setCreating(true)} className="mt-4 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="expenses-empty-new"><Plus className="h-4 w-4 mr-1" /> Saisir une dépense</Button>
        </Card>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-3" data-testid="expenses-list">
          {items.map((e) => {
            const st = EXPENSE_STATUS[e.status] || { label: e.status_label, className: 'bg-muted', Icon: FileText };
            return (
              <li key={e.id}>
                <Card className="p-4 border-border shadow-sm" data-testid="expense-item">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{e.lines[0]?.label || 'Dépense'}{e.lines.length > 1 ? ` + ${e.lines.length - 1}` : ''}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(e.date)} · <span className="font-mono">{e.ref}</span></p>
                      {e.lines[0]?.comment && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{e.lines[0].comment}</p>}
                    </div>
                    <p className="text-lg font-bold tabular-nums whitespace-nowrap" data-testid="expense-item-amount">{fmtEuro(e.total_ttc)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Badge className={`${st.className} hover:${st.className}`} data-testid="expense-status"><st.Icon className="h-3 w-3 mr-1" /> {st.label}</Badge>
                    {e.status === 'submitted' && e.submitted_at && <span className="text-xs text-muted-foreground">envoyée le {fmtDate(e.submitted_at)}</span>}
                    {e.status === 'approved' && e.approved_at && <span className="text-xs text-muted-foreground">approuvée le {fmtDate(e.approved_at)} · remboursement à venir</span>}
                    {e.status === 'paid' && <span className="text-xs text-muted-foreground">{e.paid_at ? `remboursée le ${fmtDate(e.paid_at)}` : 'remboursement effectué'}</span>}
                    {e.status === 'refused' && <span className="text-xs text-destructive">{e.refuse_reason ? `Motif : ${e.refuse_reason}` : 'Contactez l\'agence pour le motif'}</span>}
                  </div>
                  {e.photos.length > 0 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                      {e.photos.map((p) => (
                        <a key={p.index} href={photoUrl(e.local_id, p.index)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                          <img src={p.thumb} alt={`Ticket ${p.index + 1}`} className="h-16 w-16 object-cover rounded-lg border border-border" />
                        </a>
                      ))}
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <ExpenseDialog open={creating} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} onUnauthorized={onUnauthorized} />
    </div>
  );
}
